import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Compass, TrendingUp } from 'lucide-react';
import { copyVariants, staggerVariants } from './Reveal';

// Adapted from Watermelon UI block "hero-22" (warm editorial hero, italic serif emphasis, feature chips).
const features = [
  { title: 'Bespoke strategies', description: 'Tailored to your business', icon: Compass },
  { title: 'Tangible results', description: 'Measured, not promised', icon: TrendingUp },
];

const imageVariants = {
  hidden: { opacity: 0, scale: 1.08 },
  visible: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 60, damping: 20, mass: 1.2 } },
};

const featureVariants = {
  hidden: { opacity: 0, y: 22, rotateX: 18, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 240, damping: 28, mass: 0.8 },
  },
};

export default function Hero() {
  return (
    <motion.section
      className="relative isolate flex min-h-[100svh] overflow-hidden bg-paper"
      initial="hidden"
      animate="visible"
      variants={staggerVariants}
    >
      <div className="absolute inset-0 -z-20 overflow-hidden lg:left-[34%]">
        <motion.img
          src="/images/summit.jpg"
          alt="A hiker standing on a mountain ridge at sunrise, looking over the valley"
          className="pointer-events-none h-full w-full object-cover object-[60%_center] lg:object-center"
          variants={imageVariants}
        />
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-b from-paper via-paper/85 to-paper/20 lg:left-[34%] lg:w-[40%] lg:bg-gradient-to-r lg:from-paper lg:via-paper/60 lg:to-transparent"
      />

      <div className="mx-auto flex w-full max-w-7xl flex-col justify-between px-5 pb-12 pt-32 sm:px-8 lg:px-12 lg:pb-16 lg:pt-40">
        <div className="max-w-3xl">
          <motion.p variants={copyVariants} className="eyebrow">
            Digital marketing &middot; Limassol, Cyprus
          </motion.p>

          <motion.h1
            variants={copyVariants}
            className="mt-6 text-[clamp(2.9rem,6.2vw,5.6rem)] font-normal leading-[1.02] tracking-[-0.055em] text-ink text-balance"
          >
            <span className="block">Empowering Businesses,</span>
            <span className="block">
              Elevating{' '}
              <span className="font-serif text-[1.08em] italic tracking-[-0.03em] text-sage-deep">Success.</span>
            </span>
          </motion.h1>

          <motion.p
            variants={copyVariants}
            className="mt-7 max-w-xl text-[1.05rem] font-medium leading-relaxed text-ink-soft text-pretty"
          >
            We craft bespoke online marketing strategies, built on the latest trends and technologies,
            that help businesses of every size thrive in the digital landscape.
          </motion.p>

          <motion.div variants={copyVariants} className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/#contact" className="btn-primary group">
              Start a project
              <ArrowRight
                className="size-4 -rotate-45 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
            <Link to="/#services" className="btn-ghost">
              Explore our services
            </Link>
          </motion.div>
        </div>

        <motion.div
          variants={staggerVariants}
          className="mt-16 flex flex-wrap items-center gap-x-12 gap-y-6 [perspective:800px]"
        >
          {features.map(({ title, description, icon: Icon }) => (
            <motion.div key={title} variants={featureVariants} className="flex min-h-12 items-center gap-3 rounded-full bg-paper/80 p-1 pr-5 backdrop-blur-sm lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
              <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-white/50 text-forest shadow-sm outline outline-1 -outline-offset-1 outline-black/10 backdrop-blur-sm">
                <Icon className="size-5" aria-hidden="true" />
              </span>
              <span className="leading-none">
                <span className="block text-[0.9rem] font-semibold text-ink">{title}</span>
                <span className="mt-1.5 block text-[0.75rem] font-medium text-ink-soft/80">{description}</span>
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}
